import { useState } from 'react';
import {
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import {
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  Divider,
  Alert,
} from '@mantine/core';
import { IconAlertCircle, IconCreditCard, IconLock } from '@tabler/icons-react';

interface AddCardFormProps {
  onSuccess: (setupIntentId: string, nickname: string) => void;
  onCancel: () => void;
  /** Used by Stripe for 3DS return; defaults to client portal billing. */
  returnUrl?: string;
}

type FormStatus = 'idle' | 'loading' | 'submitting' | 'error';

/**
 * Form rendered inside the Stripe Elements context.
 * Mounts <PaymentElement />, handles confirmSetup, and reports back.
 */
export function AddCardForm({
  onSuccess,
  onCancel,
  returnUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/client-portal/billing`,
}: AddCardFormProps): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const [nickname, setNickname] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [elementReady, setElementReady] = useState(false);

  const isReady = stripe && elements && elementReady;
  const isSubmitting = status === 'submitting';

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();

    if (!stripe || !elements) {
      setErrorMessage('Stripe has not loaded yet. Please wait and try again.');
      return;
    }

    setStatus('submitting');
    setErrorMessage(null);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        // Required by Stripe but we handle the result in-page via redirect: 'if_required'
        return_url: returnUrl,
      },
      redirect: 'if_required',
    });

    if (error) {
      setStatus('error');
      setErrorMessage(
        error.message ?? 'An error occurred while saving your card. Please try again.'
      );
      return;
    }

    if (!setupIntent) {
      setStatus('error');
      setErrorMessage('No response received from Stripe. Please try again.');
      return;
    }

    if (setupIntent.status === 'succeeded') {
      onSuccess(setupIntent.id, nickname);
      return;
    }

    if (setupIntent.status === 'requires_action') {
      // 3DS or similar — Stripe.js handles this automatically via redirect: 'if_required'
      setStatus('error');
      setErrorMessage(
        'Additional authentication is required. Please follow the prompts from your bank.'
      );
      return;
    }

    setStatus('error');
    setErrorMessage(`Unexpected status: ${setupIntent.status}. Please try again.`);
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {errorMessage && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
            withCloseButton
            onClose={() => setErrorMessage(null)}
          >
            {errorMessage}
          </Alert>
        )}

        <Paper withBorder radius="md" p="md">
          <Stack gap="sm">
            <Group gap={6}>
              <IconLock size={14} color="var(--mantine-color-gray-6)" />
              <Text size="xs" fw={600} c="gray.6">
                Secured by Stripe
              </Text>
            </Group>

            <PaymentElement
              onReady={() => setElementReady(true)}
              onLoadError={(loadError) => {
                setStatus('error');
                setErrorMessage(
                  loadError.error?.message ?? 'Failed to load payment form. Please refresh and try again.'
                );
              }}
              options={{
                layout: 'tabs',
                wallets: { link: 'never' },
              }}
            />

            {!elementReady && (
              <Group justify="center" py="md">
                <Loader size="sm" />
                <Text size="sm" c="gray.6">Loading secure payment form...</Text>
              </Group>
            )}
          </Stack>
        </Paper>

        <TextInput
          label="Nickname (optional)"
          placeholder="e.g. Personal, HSA, Work"
          description="A friendly label to identify this card"
          value={nickname}
          onChange={(e) => setNickname(e.currentTarget.value)}
        />

        <Divider />

        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            leftSection={isSubmitting ? <Loader size={14} color="white" /> : <IconCreditCard size={16} />}
            disabled={!isReady || isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save card'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
